import type { FC } from 'react';
import { useEffect, useState, useCallback, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { debounce } from 'lodash';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { lessonToWorkout } from 'app/utils/workouts';
import {
  type LessonGroupWeekInfo,
  SelectDirection,
  type WeeklyWorkoutListWrapperQuery,
  type WeeklyWorkoutListWrapperQueryVariables,
} from 'app/types/graphql';

import { WeeklyWorkoutListWrapper as WORKOUTS } from 'app/ui/components/molecules/weekly-workout-list-wrapper/workouts.gql';

import Loading from 'app/ui/components/atoms/loading';
import ErrorComponent from 'app/ui/components/atoms/error';
import Typography from 'app/ui/components/atoms/typography';

import type { Workout } from 'app/ui/components/molecules/workout-list';
import WorkoutRail from 'app/ui/components/molecules/workout-rail';

type LessonGroup = {
  workouts: Workout[];
  weekInfo: LessonGroupWeekInfo;
};

const Week = styled.span`
  font-weight: bold;
`;

const RailTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

const WeeklyWorkoutListWrapper: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);

  const targetWeekParam = queryParams.get('week');
  const targetWeek = targetWeekParam ? Number(targetWeekParam) : null;
  const maxWeekParam = queryParams.get('maxWeek');
  const maxWeek = maxWeekParam ? Number(maxWeekParam) : null;

  const intl = useIntl();
  const theme = useTheme();

  const hasScrolledToTarget = useRef<boolean>(false);
  const targetWeekRef = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const defaultBatchSize = 3;

  const getBatchSize = useCallback(() => {
    if (!targetWeek || !maxWeek) {
      return defaultBatchSize;
    }

    // + 2 to get the week itself and one below for the scroll to work well and bring the rail to the top
    return maxWeek - targetWeek + 2;
  }, [targetWeek, maxWeek]);

  const [offset, setOffset] = useState<number>(0);
  const [groups, setGroups] = useState<LessonGroup[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentMaxWeek, setCurrentMaxWeek] = useState<number | null>(maxWeek);
  const [initialOffsetApplied, setInitialOffsetApplied] = useState(false);

  const queryVariables: WeeklyWorkoutListWrapperQueryVariables = {
    filters: {
      first: getBatchSize(),
      offset,
      orderBy: 'weekNumber',
      direction: SelectDirection.desc,
    },
  };

  const { data, loading, error } = useQuery<WeeklyWorkoutListWrapperQuery, WeeklyWorkoutListWrapperQueryVariables>(
    WORKOUTS,
    {
      variables: queryVariables,
      fetchPolicy: 'no-cache',
    },
  );

  useEffect(() => {
    if (data?.lessonGroups?.nodes) {
      const newGroups = data.lessonGroups.nodes.map(({ lessons, weekInfo }) => {
        const workouts = lessons?.map((lesson) => lessonToWorkout(lesson)) || [];
        return { workouts, weekInfo } as LessonGroup;
      });
      setGroups((prevGroups) => [...prevGroups, ...newGroups]);
      setLoadingMore(false);

      if (newGroups.length > 0 && !currentMaxWeek) {
        const highestWeekNumber = newGroups[0]?.weekInfo?.number;
        if (highestWeekNumber) {
          setCurrentMaxWeek(highestWeekNumber);
        }
      }
    }
  }, [data]);

  useEffect(() => {
    if (targetWeek && !hasScrolledToTarget.current && targetWeekRef.current[targetWeek]) {
      setTimeout(() => {
        const element = targetWeekRef.current[targetWeek];
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: 'smooth' });
          hasScrolledToTarget.current = true;
        }
      }, 100);
    }
  }, [groups, targetWeek]);

  const loadMore = useCallback(() => {
    if (loadingMore) {
      return;
    }

    setLoadingMore(true);
    setOffset((prevOffset) => {
      if (!initialOffsetApplied) {
        setInitialOffsetApplied(true);
        return prevOffset + getBatchSize();
      }
      return prevOffset + defaultBatchSize;
    });
  }, [loadingMore, defaultBatchSize, getBatchSize, initialOffsetApplied]);

  const debouncedHandleScroll = useCallback(
    debounce(() => {
      const scrollTop = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.offsetHeight;

      if (scrollTop >= documentHeight - 100) {
        loadMore();
      }
    }, 200),
    [loadMore],
  );

  useEffect(() => {
    window.addEventListener('scroll', debouncedHandleScroll);
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, [debouncedHandleScroll]);

  const handleCardClick = useCallback(
    (weekNumber: number) => {
      const maxWeekNumber = currentMaxWeek || (groups[0]?.weekInfo?.number ?? null);

      const newParams = new URLSearchParams(location.search);
      newParams.set('week', weekNumber.toString());

      if (maxWeekNumber) {
        newParams.set('maxWeek', maxWeekNumber.toString());
      }

      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    },
    [location.pathname, location.search, navigate, groups, currentMaxWeek],
  );

  if (loading && groups.length === 0) {
    return <Loading />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  const title = intl.formatMessage({ id: 'workoutLibrary.weekly' });
  const weekText = intl.formatMessage({ id: 'home.copy.week' });

  return (
    <>
      <Typography variant="h3">{title}</Typography>
      {groups.map(({ workouts, weekInfo }) => (
        <div
          key={`week-${weekInfo?.number}`}
          ref={(el) => {
            if (weekInfo?.number) {
              targetWeekRef.current[weekInfo.number] = el;
            }
          }}
        >
          <WorkoutRail
            title={
              <RailTitle>
                <Typography variant="body1">
                  <Week>
                    {weekText} {weekInfo?.number}
                  </Week>
                </Typography>
                <Typography variant="body3" color={theme.colors.knop}>
                  {moment.utc(weekInfo?.from).format('DD/MM')}-{moment.utc(weekInfo?.to).format('DD/MM YYYY')}
                </Typography>
              </RailTitle>
            }
            workouts={workouts}
            testId={`rail-week-${weekInfo?.number}`}
            cardTestIdPrefix="weekly-list-card"
            onCardClick={() => handleCardClick(weekInfo.number)}
          />
        </div>
      ))}
    </>
  );
};

export default WeeklyWorkoutListWrapper;
